import {
    ResolvedPersonLiteDto,
    ConnectionDetailLiteDto,
} from '../api/network/models/getNetworkScopedDtos'
import { SharedWorkHistoryLiteDto } from '../api/role/models/SharedWorkHistoryDto'
import {
    TransformedResolvedPerson,
    TransformedCompany,
    TransformedConnection,
} from './HelixSearchTypes'

export class HelixSearchTransformer {
    /**
     * Transforms network data from backend structure to UI-friendly grouped structure
     * Groups connection details by company and organizes seed person connections
     */
    public static transformNetworkData(
        resolvedPersonLites: ResolvedPersonLiteDto[],
    ): TransformedResolvedPerson[] {
        const now = new Date()

        return resolvedPersonLites.map((resolvedPerson) => {
            // Grouping: companyId -> seedPersonId -> shared history
            const companyMap = new Map<
                string,
                Map<string, SharedWorkHistoryLiteDto[]>
            >()

            // Track relationship info by seedPersonId for this resolved person
            const relationshipInfoMap = new Map<
                string,
                ConnectionDetailLiteDto
            >()

            for (const connection of resolvedPerson.connectionDetails) {
                const seedPersonId = connection.seedPersonId
                relationshipInfoMap.set(seedPersonId, connection)

                for (const swh of connection.sharedWorkHistory) {
                    const { companyId } = swh

                    const companyEntry =
                        companyMap.get(companyId) ??
                        new Map<string, SharedWorkHistoryLiteDto[]>()
                    const existing = companyEntry.get(seedPersonId) ?? []

                    existing.push(swh)
                    companyEntry.set(seedPersonId, existing)
                    companyMap.set(companyId, companyEntry)
                }
            }

            const companies: TransformedCompany[] = Array.from(
                companyMap.entries(),
            ).map(([companyId, seedMap]) => {
                const connections: TransformedConnection[] = Array.from(
                    seedMap.entries(),
                ).map(([seedPersonId, history]) => {
                    const resolvedTitles: string[] = []
                    const seedTitles: string[] = []
                    const overlaps: { start: Date; end: Date }[] = []

                    for (const swh of history) {
                        if (
                            !swh.resolvedPersonWorkedAt.startDate ||
                            !swh.seedPersonWorkedAt.startDate
                        ) {
                            continue
                        }

                        resolvedTitles.push(swh.resolvedPersonWorkedAt.jobTitle)
                        seedTitles.push(swh.seedPersonWorkedAt.jobTitle)

                        const start = new Date(swh.startOfOverlap ?? now)
                        const end = new Date(swh.endOfOverlap ?? now)

                        overlaps.push({ start, end })
                    }

                    const uniqueResolved = [...new Set(resolvedTitles)]
                    const uniqueSeed = [...new Set(seedTitles)]

                    let cumulativeOverlapMs = 0
                    let earliestStart = now
                    let latestEnd = new Date(0)

                    for (const { start, end } of overlaps) {
                        cumulativeOverlapMs += end.getTime() - start.getTime()
                        if (start < earliestStart) earliestStart = start
                        if (end > latestEnd) latestEnd = end
                    }

                    return {
                        seedPersonId,
                        resolvedPersonJobTitles: uniqueResolved,
                        seedPersonJobTitles: uniqueSeed,
                        cumulativeOverlapMs,
                        overallStart: earliestStart,
                        overallEnd: latestEnd,
                        relationshipInfo:
                            relationshipInfoMap.get(seedPersonId)
                                ?.relationshipInfo,
                    }
                })

                // Sort connections by most recent end date of overlap
                connections.sort(
                    (a, b) => b.overallEnd.getTime() - a.overallEnd.getTime(),
                )

                return {
                    companyId,
                    connections,
                }
            })

            // Sort companies by most recent overallEnd (max of all connections)
            companies.sort((a, b) => {
                const aLatest = Math.max(
                    ...a.connections.map((c) => c.overallEnd.getTime()),
                )
                const bLatest = Math.max(
                    ...b.connections.map((c) => c.overallEnd.getTime()),
                )
                return bLatest - aLatest
            })

            return {
                personId: resolvedPerson.personId,
                companies,
            }
        })
    }

    /**
     * Transforms a single person's connection details for use in components
     * This is useful when you have individual connection details rather than the full network response
     */
    public static transformSinglePersonConnections(
        personId: string,
        connectionDetails: ConnectionDetailLiteDto[],
    ): TransformedResolvedPerson {
        return this.transformNetworkData([
            {
                personId,
                connectionDetails,
                companyConnectionDetails: [], // Not used in this transformation
            },
        ])[0]
    }
}
